// ===============================
// PlanningEditorOph.jsx
// ===============================
// Vue hebdomadaire du planning OPH
// Editeur de trame hebdomadaire OPH.
// Données 100% indépendantes : endpoints /api/v2/trame-hebdo-oph/
// ===============================

import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { confirmDialog } from "../ui/dialogService.js";
import { apiFetch } from "../apiMedecins.js";
import { isFrenchPublicHoliday } from "../frenchHolidays.mjs";

const COULEURS_ACTIVITES = {
  "Salle Robot 10": { fond: "#c6efce", texte: "#006100" },
  "Salle DIG 8": { fond: "#c6efce", texte: "#006100" },
  "Salle DIG 7": { fond: "#c6efce", texte: "#006100" },
  "Salle DIG 6": { fond: "#c6efce", texte: "#006100" },

  "SSPI 14310": { fond: "#ffc7ce", texte: "#9c0006" },

  "CPA DIG Ollier": { fond: "#e4dfec", texte: "#604a7b" },
  "CPA ENDO Achard": { fond: "#e4dfec", texte: "#604a7b" },

  "ENDO KT/EE": { fond: "#ffeb9c", texte: "#9c6500" },
  "ENDO MUCO": { fond: "#ffeb9c", texte: "#9c6500" },
  "RADIO VASC": { fond: "#ffeb9c", texte: "#9c6500" },
  "RADIO VASC 2": { fond: "#ffeb9c", texte: "#9c6500" },

  "AIDE OPH": { fond: "#ddebf7", texte: "#0c5480" },
  "AIDE UCA": { fond: "#ddebf7", texte: "#0c5480" },
  "AIDE THO": { fond: "#ddebf7", texte: "#0c5480" },

  "GESTION/RÉUNION": { fond: "#ffd8b1", texte: "#974806" },

  "Garde Digestif": { fond: "#f4cccc", texte: "#85200c" },
  "Garde Interne": { fond: "#f4cccc", texte: "#85200c" },
  "Garde REA": { fond: "#f4cccc", texte: "#85200c" },
  "Garde PR 2": { fond: "#f4cccc", texte: "#85200c" },
  "Astreinte THO": { fond: "#f4cccc", texte: "#85200c" },

  "Repos de garde": { fond: "#d9d9e3", texte: "#3d3d5c" },

  NC: { fond: "#a6d5d8", texte: "#1e5d61" },

  "CA/RTT": { fond: "#d5c5e8", texte: "#5b3d7a" },

  CF: { fond: "#d4e8d4", texte: "#2d5a2d" },

  AIG: { fond: "#d4e8d4", texte: "#2d5a2d" },
};

function getCouleursActivite(activite) {
  const activiteBase = activite.replace(/ \d+$/, "");
  return (
    COULEURS_ACTIVITES[activiteBase] ||
    COULEURS_ACTIVITES[activite] || {
      fond: "#ffffff",
      texte: "#000000",
    }
  );
}

function getNumeroSemaineISO(dateStr) {
  const d = new Date(dateStr);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 4 - (d.getDay() || 7));
  const yearStart = new Date(d.getFullYear(), 0, 1);
  const weekNo = Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
  return weekNo;
}

function getDatesFromMoisSemaine(annee, mois, numeroSemaine) {
  const premierJour = new Date(annee, mois - 1, 1);
  const jourSemaine = premierJour.getDay();
  const decalage =
    jourSemaine === 0 ? 1 : jourSemaine === 1 ? 0 : 8 - jourSemaine;
  const premierLundi = new Date(
    annee,
    mois - 1,
    1 + decalage
  );
  const lundiCible = new Date(premierLundi);
  lundiCible.setDate(lundiCible.getDate() + (numeroSemaine - 1) * 7);

  const jours = [];
  const nomsJours = [
    "Lundi",
    "Mardi",
    "Mercredi",
    "Jeudi",
    "Vendredi",
    "Samedi",
    "Dimanche",
  ];

  for (let i = 0; i < 7; i++) {
    const jour = new Date(lundiCible);
    jour.setDate(jour.getDate() + i);
    const jourNum = String(jour.getDate()).padStart(2, "0");
    const moisNum = String(jour.getMonth() + 1).padStart(2, "0");
    jours.push({
      jour: nomsJours[i],
      date: `${jourNum}/${moisNum}`,
      iso: `${jour.getFullYear()}-${moisNum}-${jourNum}`,
    });
  }

  return jours;
}

function getNomMois(numeroMois) {
  const mois = [
    "Janvier",
    "Février",
    "Mars",
    "Avril",
    "Mai",
    "Juin",
    "Juillet",
    "Août",
    "Septembre",
    "Octobre",
    "Novembre",
    "Décembre",
  ];
  return mois[numeroMois - 1];
}

function getNombreSemainesDansMois(annee, mois) {
  let n = 1;
  while (n <= 6) {
    const jours = getDatesFromMoisSemaine(annee, mois, n);
    const aDesJoursDansMois = jours.some((j) => {
      const d = new Date(j.iso);
      return d.getMonth() + 1 === mois;
    });
    if (!aDesJoursDansMois) {
      return n - 1;
    }
    n++;
  }
  return 6;
}

export default function PlanningEditorOph() {
  const navigate = useNavigate();
  const params = useParams();

  const annee = params.annee ? parseInt(params.annee, 10) : 2026;
  const mois = params.mois ? parseInt(params.mois, 10) : 2;
  const numeroSemaine = params.semaine ? parseInt(params.semaine, 10) : 1;

  const [message, setMessage] = useState("");
  const [trame, setTrame] = useState({});
  const [isLoaded, setIsLoaded] = useState(false);
  const [sourceChargement, setSourceChargement] = useState("");

  const trameRef = useRef(trame);
  trameRef.current = trame;

  const joursData = getDatesFromMoisSemaine(annee, mois, numeroSemaine);
  const numeroISO = getNumeroSemaineISO(joursData[0].iso);
  const nomMois = getNomMois(mois);
  const identifiantSemaine = `${annee}-${String(mois).padStart(
    2,
    "0"
  )}_semaine-${numeroSemaine}`;

  const identifiantSemaineRef = useRef(identifiantSemaine);
  identifiantSemaineRef.current = identifiantSemaine;

  const persistTimerRef = useRef(null);
  const isLoadedRef = useRef(false);
  isLoadedRef.current = isLoaded;

  const jours = joursData.map((j) => ({
    ...j,
    type:
      ["Samedi", "Dimanche"].includes(j.jour) ||
      isFrenchPublicHoliday(j.iso)
        ? "fusion"
        : "semaine",
  }));

  const activitesNormales = [
    "Salle Robot 10",
    "Salle DIG 8",
    "Salle DIG 7",
    "Salle DIG 6",
    "SSPI 14310",
    "CPA DIG Ollier",
    "CPA ENDO Achard",
    "ENDO KT/EE",
    "ENDO MUCO",
    "RADIO VASC",
    "RADIO VASC 2",
    "AIDE OPH",
    "AIDE UCA",
    "AIDE THO",
    "GESTION/RÉUNION",
  ];

  const activitesFusionWeekEnd = [...activitesNormales, "Garde Digestif"];
  const gardesEtAstreintes = [
    "Garde Digestif",
    "Garde Interne",
    "Garde REA",
    "Garde PR 2",
    "Astreinte THO",
    "Repos de garde",
  ];
  const autresLignes = ["NC", "CA/RTT", "CF", "AIG"];

  const estGardeOuAstreinte = (activite) => {
    const activiteBase = activite.replace(" 2", "");
    return (
      gardesEtAstreintes.includes(activiteBase) ||
      gardesEtAstreintes.includes(activite)
    );
  };

  const estDansFusionWeekEnd = (activite) => {
    const activiteBase = activite.replace(" 2", "");
    return activitesFusionWeekEnd.includes(activiteBase);
  };

  const allerSemainePrecedente = () => {
    let nouvelleSemaine = numeroSemaine - 1;
    let nouveauMois = mois;
    let nouvelleAnnee = annee;

    if (nouvelleSemaine < 1) {
      nouveauMois = mois - 1;
      if (nouveauMois < 1) {
        nouveauMois = 12;
        nouvelleAnnee = annee - 1;
      }
      const nbSemainesMoisPrecedent = getNombreSemainesDansMois(
        nouvelleAnnee,
        nouveauMois
      );
      nouvelleSemaine = nbSemainesMoisPrecedent || 1;
    }

    navigate(
      `/responsable/oph-planning/${nouvelleAnnee}/${nouveauMois}/${nouvelleSemaine}`
    );
  };

  const allerSemaineSuivante = () => {
    const nbSemainesMoisCourant = getNombreSemainesDansMois(annee, mois);
    let nouvelleSemaine = numeroSemaine + 1;
    let nouveauMois = mois;
    let nouvelleAnnee = annee;

    if (nouvelleSemaine > nbSemainesMoisCourant) {
      nouveauMois = mois + 1;
      if (nouveauMois > 12) {
        nouveauMois = 1;
        nouvelleAnnee = annee + 1;
      }
      nouvelleSemaine = 1;
    }

    navigate(
      `/responsable/oph-planning/${nouvelleAnnee}/${nouveauMois}/${nouvelleSemaine}`
    );
  };

  const retourGestionPlanning = () => {
    if (window.opener) {
      window.close();
    } else {
      navigate("/responsable/oph");
    }
  };

  useEffect(() => {
    setIsLoaded(false);
    if (persistTimerRef.current) {
      clearTimeout(persistTimerRef.current);
      persistTimerRef.current = null;
    }
    const chargerTrame = async () => {
      try {
        const resSemaine = await apiFetch(
          `/api/v2/trame-hebdo-oph/${encodeURIComponent(identifiantSemaine)}`,
          { credentials: "include" }
        );

        if (resSemaine.ok) {
          const data = await resSemaine.json();
          setTrame(data);
          setSourceChargement("semaine");
          setMessage(
            `✅ ${nomMois} ${annee} - Semaine ${numeroSemaine} validée.`
          );
          setIsLoaded(true);
          return;
        }

        const resModele = await apiFetch(
          "/api/v2/trame-hebdo-oph/modele",
          { credentials: "include" }
        );

        if (resModele.ok) {
          const modele = await resModele.json();
          setTrame(modele);
          setSourceChargement("modele");
          setMessage(
            "ℹ️ Semaine non encore validée (modèle durable affiché)."
          );
          setIsLoaded(true);
          return;
        }

        throw new Error("Impossible de charger une trame");
      } catch (err) {
        console.error("Erreur chargement trame :", err);
        setMessage("❌ Erreur de chargement.");
        setIsLoaded(true);
      }
    };

    chargerTrame();
  }, [annee, mois, numeroSemaine, identifiantSemaine, nomMois]);

  const flushPersistTrameSemaine = async () => {
    if (!isLoadedRef.current) return;
    const contenu = trameRef.current;
    const semaine = identifiantSemaineRef.current;
    try {
      const res = await apiFetch("/api/v2/trame-hebdo-oph/ponctuelle", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ semaine, contenu }),
      });
      if (!res.ok) {
        const t = await res.text().catch(() => "");
        throw new Error(t || `HTTP ${res.status}`);
      }
      setSourceChargement("semaine");
      setMessage(
        `✅ Semaine validée : ${nomMois} ${annee} - Semaine ${numeroSemaine}.`
      );
    } catch (err) {
      console.error("Autosave trame OPH :", err);
      setMessage("❌ Erreur d'enregistrement automatique.");
    }
  };

  const schedulePersistTrameSemaine = () => {
    if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
    persistTimerRef.current = setTimeout(() => {
      persistTimerRef.current = null;
      flushPersistTrameSemaine();
    }, 600);
  };

  const chargerModele = async () => {
    try {
      const res = await apiFetch("/api/v2/trame-hebdo-oph/modele", { credentials: "include" });
      if (!res.ok) throw new Error("Erreur");
      const modele = await res.json();
      setTrame(modele);
      setSourceChargement("modele");
      setMessage("ℹ️ Semaine non encore validée (modèle durable affiché).");
      setTimeout(() => schedulePersistTrameSemaine(), 0);
    } catch (err) {
      setMessage("❌ Erreur.");
    }
  };

  const reinitialiserModele = async () => {
    if (!(await confirmDialog("⚠️ Réinitialiser le modèle durable ?"))) return;

    try {
      const res = await apiFetch(
        "/api/v2/trame-hebdo-oph/modele/init",
        { method: "POST", credentials: "include" }
      );
      if (!res.ok) throw new Error("Erreur");
      const json = await res.json();
      setTrame(json.modele);
      setSourceChargement("modele");
      setMessage(
        "ℹ️ Semaine non encore validée (modèle durable affiché après réinitialisation)."
      );
      setTimeout(() => schedulePersistTrameSemaine(), 0);
    } catch (err) {
      setMessage("❌ Erreur.");
    }
  };

  const toggleCell = (activite, jour, moment) => {
    const key = `${activite}_${jour}_${moment}`;
    setTrame((prev) => {
      const next = {
        ...prev,
        [key]: prev[key] === "ouvert" ? "fermé" : "ouvert",
      };
      trameRef.current = next;
      return next;
    });
    schedulePersistTrameSemaine();
  };

  const saveModeleDurable = async () => {
    try {
      const res = await apiFetch("/api/v2/trame-hebdo-oph/modele", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(trameRef.current),
      });

      if (!res.ok) throw new Error("Erreur");

      setMessage("📦 Modèle durable mis à jour.");
    } catch (err) {
      setMessage("❌ Erreur.");
    }
  };

  const estSemaineValidee = sourceChargement === "semaine";

  const S = {
    container: {
      maxWidth: "98%",
      margin: "20px auto",
      fontFamily:
        "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    },
    navBar: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "20px",
      padding: "18px 28px",
      borderRadius: "16px",
      boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
    },
    btnRetour: {
      padding: "12px 24px",
      background: "#ffffff",
      color: "#000000",
      border: "1px solid #d1d5db",
      borderRadius: "10px",
      cursor: "pointer",
      fontWeight: "900",
      fontSize: "26px",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      transition: "all 0.3s ease",
    },
    semaineInfo: {
      textAlign: "center",
      color: "white",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: "6px",
    },
    semaineTitleRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexWrap: "wrap",
      gap: "16px",
    },
    titreSemaine: {
      fontSize: "34px",
      fontWeight: "900",
      textShadow: "0 2px 4px rgba(0,0,0,0.1)",
    },
    badgeEtat: {
      padding: "6px 16px",
      borderRadius: "999px",
      fontSize: "20px",
      fontWeight: "900",
      textTransform: "uppercase",
      border: "2px solid rgba(0,0,0,0.1)",
      background: "#e5e7eb",
      color: "#111827",
    },
    datesSemaine: {
      fontSize: "24px",
      fontWeight: "700",
      opacity: 0.98,
    },
    navigationSemaines: {
      display: "flex",
      gap: "12px",
    },
    btnNav: {
      padding: "12px 20px",
      background: "#ffffff",
      color: "#000000",
      border: "1px solid #d1d5db",
      borderRadius: "10px",
      cursor: "pointer",
      fontWeight: "900",
      fontSize: "26px",
      transition: "all 0.3s ease",
    },
    tableWrap: {
      overflowX: "auto",
      background: "#fff",
      borderRadius: "16px",
      boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
      border: "3px solid #333",
      marginTop: "20px",
    },
    table: {
      width: "100%",
      borderCollapse: "separate",
      borderSpacing: 0,
      fontSize: "16px",
    },
    th: {
      background: "linear-gradient(180deg, #f9fafb 0%, #f3f4f6 100%)",
      border: "3px solid #333",
      padding: "12px 10px",
      textAlign: "center",
      fontWeight: "900",
      fontSize: "20px",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
      color: "#000000",
      whiteSpace: "nowrap",
      position: "sticky",
      top: 0,
      zIndex: 10,
    },
    td: {
      border: "3px solid #333",
      padding: "12px 14px",
      textAlign: "center",
      cursor: "pointer",
      transition: "all 0.2s ease",
      fontSize: "20px",
      fontWeight: "900",
      whiteSpace: "nowrap",
      position: "relative",
    },
    thActivite: {
      textAlign: "left",
      fontWeight: "900",
      paddingLeft: "16px",
      width: "1%",
      whiteSpace: "nowrap",
      fontSize: "20px",
    },
    tdActivite: {
      textAlign: "left",
      fontWeight: "900",
      paddingLeft: "16px",
      position: "sticky",
      left: 0,
      zIndex: 5,
      whiteSpace: "nowrap",
      fontSize: "26px",
      boxShadow: "2px 0 4px rgba(0,0,0,0.05)",
      width: "1%",
    },
    ampmHead: {
      background: "linear-gradient(180deg, #eef2ff 0%, #e0e7ff 100%)",
      fontWeight: "900",
      fontSize: "14px",
      color: "#000000",
      width: "90px",
      minWidth: "90px",
      maxWidth: "90px",
    },
    tdCell: {
      width: "90px",
      minWidth: "90px",
      maxWidth: "90px",
    },
    tdWeekend: {
      width: "90px",
      minWidth: "90px",
      maxWidth: "90px",
    },
    btnZone: {
      display: "flex",
      justifyContent: "center",
      flexWrap: "wrap",
      gap: "14px",
      marginTop: "28px",
    },
    btnAction: {
      padding: "14px 28px",
      borderRadius: "10px",
      border: "3px solid #000000",
      cursor: "pointer",
      color: "#000000",
      background: "#ffffff",
      fontWeight: "900",
      fontSize: "24px",
      transition: "all 0.3s ease",
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
    message: {
      textAlign: "center",
      marginTop: "18px",
      fontSize: "22px",
      fontWeight: "700",
    },
  };

  const getIndexDansFusion = (a) => activitesFusionWeekEnd.indexOf(a);

  const renderLigneActivite = (a, indexFusion) => {
    const isGarde = estGardeOuAstreinte(a);
    const isDansFusion = estDansFusionWeekEnd(a);
    const couleurs = getCouleursActivite(a);

    return (
      <tr key={a}>
        <td
          style={{
            ...S.td,
            ...S.tdActivite,
            background: couleurs.fond,
            color: couleurs.texte,
          }}
        >
          {a}
        </td>
        {jours.map((j) => {
          if (j.type === "semaine") {
            if (isGarde) {
              const key = `${a}_${j.jour}_journée`;
              const isOuvert = trame[key] === "ouvert";
              const isFerme = trame[key] === "fermé";

              return (
                <td
                  key={key}
                  colSpan={2}
                  style={{
                    ...S.td,
                    background: isFerme ? "#333333" : couleurs.fond,
                    color: isFerme ? "#ffffff" : couleurs.texte,
                  }}
                  onClick={() => toggleCell(a, j.jour, "journée")}
                  onMouseEnter={(e) => {
                    if (!isFerme) {
                      e.target.style.boxShadow = "inset 0 0 0 4px #3b82f6";
                      e.target.style.transform = "scale(1.02)";
                      e.target.style.zIndex = "3";
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.boxShadow = "none";
                    e.target.style.transform = "scale(1)";
                    e.target.style.zIndex = "1";
                  }}
                >
                  <span style={{ fontSize: "19px" }}>
                    {isFerme ? "" : isOuvert ? "✓" : ""}
                  </span>
                </td>
              );
            }

            return (
              <React.Fragment key={j.jour}>
                <td
                  style={{
                    ...S.td,
                    ...S.tdCell,
                    background:
                      trame[`${a}_${j.jour}_Matin`] === "fermé"
                        ? "#938c8cff"
                        : couleurs.fond,
                    color:
                      trame[`${a}_${j.jour}_Matin`] === "fermé"
                        ? "#ffffff"
                        : couleurs.texte,
                  }}
                  onClick={() => toggleCell(a, j.jour, "Matin")}
                  onMouseEnter={(e) => {
                    if (trame[`${a}_${j.jour}_Matin`] !== "fermé") {
                      e.target.style.boxShadow = "inset 0 0 0 4px #3b82f6";
                      e.target.style.transform = "scale(1.02)";
                      e.target.style.zIndex = "3";
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.boxShadow = "none";
                    e.target.style.transform = "scale(1)";
                    e.target.style.zIndex = "1";
                  }}
                >
                  <span style={{ fontSize: "19px" }}>
                    {trame[`${a}_${j.jour}_Matin`] === "fermé"
                      ? ""
                      : trame[`${a}_${j.jour}_Matin`] === "ouvert"
                      ? "✓"
                      : ""}
                  </span>
                </td>
                <td
                  style={{
                    ...S.td,
                    ...S.tdCell,
                    background:
                      trame[`${a}_${j.jour}_Après-midi`] === "fermé"
                        ? "#938c8cff"
                        : couleurs.fond,
                    color:
                      trame[`${a}_${j.jour}_Après-midi`] === "fermé"
                        ? "#ffffff"
                        : couleurs.texte,
                  }}
                  onClick={() => toggleCell(a, j.jour, "Après-midi")}
                  onMouseEnter={(e) => {
                    if (trame[`${a}_${j.jour}_Après-midi`] !== "fermé") {
                      e.target.style.boxShadow = "inset 0 0 0 4px #3b82f6";
                      e.target.style.transform = "scale(1.02)";
                      e.target.style.zIndex = "3";
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.boxShadow = "none";
                    e.target.style.transform = "scale(1)";
                    e.target.style.zIndex = "1";
                  }}
                >
                  <span style={{ fontSize: "19px" }}>
                    {trame[`${a}_${j.jour}_Après-midi`] === "fermé"
                      ? ""
                      : trame[`${a}_${j.jour}_Après-midi`] === "ouvert"
                      ? "✓"
                      : ""}
                  </span>
                </td>
              </React.Fragment>
            );
          } else {
            if (isDansFusion) {
              if (indexFusion === 0) {
                const key = `ActivitésFusion_${j.jour}_Présence`;
                const isFerme = trame[key] === "fermé";
                const isOuvert = trame[key] === "ouvert";
                const couleurWeekend = { fond: "#c6efce", texte: "#006100" };

                return (
                  <td
                    key={key}
                    rowSpan={activitesFusionWeekEnd.length}
                    style={{
                      ...S.td,
                      ...S.tdWeekend,
                      background: isFerme ? "#333333" : couleurWeekend.fond,
                      color: isFerme ? "#ffffff" : couleurWeekend.texte,
                    }}
                    onClick={() =>
                      toggleCell("ActivitésFusion", j.jour, "Présence")
                    }
                    onMouseEnter={(e) => {
                      if (!isFerme) {
                        e.target.style.boxShadow = "inset 0 0 0 4px #3b82f6";
                        e.target.style.transform = "scale(1.02)";
                        e.target.style.zIndex = "3";
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.boxShadow = "none";
                      e.target.style.transform = "scale(1)";
                      e.target.style.zIndex = "1";
                    }}
                  >
                    <span style={{ fontSize: "19px" }}>
                      {isFerme ? "" : isOuvert ? "✓" : ""}
                    </span>
                  </td>
                );
              }
              return null;
            }

            const key = `${a}_${j.jour}_journée`;
            const isFerme = trame[key] === "fermé";
            const isOuvert = trame[key] === "ouvert";

            return (
              <td
                key={key}
                style={{
                  ...S.td,
                  ...S.tdWeekend,
                  background: isFerme ? "#333333" : couleurs.fond,
                  color: isFerme ? "#ffffff" : couleurs.texte,
                }}
                onClick={() => toggleCell(a, j.jour, "journée")}
                onMouseEnter={(e) => {
                  if (!isFerme) {
                    e.target.style.boxShadow = "inset 0 0 0 4px #3b82f6";
                    e.target.style.transform = "scale(1.02)";
                    e.target.style.zIndex = "3";
                  }
                }}
                onMouseLeave={(e) => {
                  e.target.style.boxShadow = "none";
                  e.target.style.transform = "scale(1)";
                  e.target.style.zIndex = "1";
                }}
              >
                <span style={{ fontSize: "19px" }}>
                  {isFerme ? "" : isOuvert ? "✓" : ""}
                </span>
              </td>
            );
          }
        })}
      </tr>
    );
  };

  if (!isLoaded) {
    return (
      <div style={{ padding: 40, textAlign: "center", fontSize: 24 }}>
        ⏳ Chargement du planning OPH...
      </div>
    );
  }

  return (
    <div style={S.container}>
      <div
        style={{
          ...S.navBar,
          background: estSemaineValidee
            ? "linear-gradient(135deg, #16a34a 0%, #22c55e 100%)"
            : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        }}
      >
        <button
          style={S.btnRetour}
          onClick={retourGestionPlanning}
          onMouseOver={(e) => (e.target.style.background = "#f3f4f6")}
          onMouseOut={(e) => (e.target.style.background = "#ffffff")}
        >
          <span style={{ fontSize: "20px" }}>←</span> Retour
        </button>

        <div style={S.semaineInfo}>
          <div style={S.semaineTitleRow}>
            <div style={S.titreSemaine}>
              👁️ Planning OPH - {nomMois} {annee} - Semaine {numeroSemaine}
            </div>
            <div
              style={{
                ...S.badgeEtat,
                backgroundColor: estSemaineValidee ? "#bbf7d0" : "#fee2e2",
                color: estSemaineValidee ? "#166534" : "#991b1b",
                borderColor: estSemaineValidee ? "#16a34a" : "#f97373",
              }}
            >
              {estSemaineValidee
                ? "Semaine validée"
                : "Semaine non encore validée"}
            </div>
          </div>
          <div style={S.datesSemaine}>
            Du {jours[0].date} au {jours[6].date} (Semaine ISO {numeroISO})
          </div>
        </div>

        <div style={S.navigationSemaines}>
          <button
            style={S.btnNav}
            onClick={allerSemainePrecedente}
            onMouseOver={(e) => (e.target.style.background = "#f3f4f6")}
            onMouseOut={(e) => (e.target.style.background = "#ffffff")}
          >
            ◀ Précédent
          </button>
          <button
            style={S.btnNav}
            onClick={allerSemaineSuivante}
            onMouseOver={(e) => (e.target.style.background = "#f3f4f6")}
            onMouseOut={(e) => (e.target.style.background = "#ffffff")}
          >
            Suivant ▶
          </button>
        </div>
      </div>

      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead>
            <tr>
              <th style={{ ...S.th, ...S.thActivite }}>ACTIVITÉ</th>
              {jours.map((j) => (
                <th
                  key={j.jour}
                  colSpan={j.type === "semaine" ? 2 : 1}
                  style={S.th}
                >
                  {j.jour.toUpperCase()}
                  <br />
                  <span
                    style={{ fontWeight: "900", fontSize: "22px" }}
                  >
                    {j.date}
                  </span>
                </th>
              ))}
            </tr>
            <tr>
              <th style={S.th}></th>
              {jours.map((j) =>
                j.type === "semaine" ? (
                  <React.Fragment key={j.jour}>
                    <th style={{ ...S.th, ...S.ampmHead }}>MATIN</th>
                    <th style={{ ...S.th, ...S.ampmHead }}>APRÈS-MIDI</th>
                  </React.Fragment>
                ) : (
                  <th
                    key={j.jour}
                    style={{ ...S.th, ...S.ampmHead }}
                  >
                    JOURNÉE
                  </th>
                )
              )}
            </tr>
          </thead>
          <tbody>
            {activitesNormales.map((a) => {
              const indexFusion = getIndexDansFusion(a);
              return (
                <React.Fragment key={a}>
                  {renderLigneActivite(a, indexFusion)}
                </React.Fragment>
              );
            })}

            {renderLigneActivite(
              "Garde Digestif",
              getIndexDansFusion("Garde Digestif")
            )}

            {gardesEtAstreintes
              .filter((g) => g !== "Garde Digestif")
              .map((a) => (
                <React.Fragment key={a}>
                  {renderLigneActivite(a, -1)}
                </React.Fragment>
              ))}

            {autresLignes.map((a) => (
              <React.Fragment key={a}>
                {renderLigneActivite(a, -1)}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <div style={S.btnZone}>
        <button
          style={S.btnAction}
          onClick={() => saveModeleDurable()}
          onMouseOver={(e) => {
            e.target.style.transform = "translateY(-2px)";
            e.target.style.background = "#f3f4f6";
          }}
          onMouseOut={(e) => {
            e.target.style.transform = "translateY(0)";
            e.target.style.background = "#ffffff";
          }}
        >
          <span style={{ fontSize: "22px" }}>📦</span> Enregistrer comme
          modèle
        </button>
        <button
          style={S.btnAction}
          onClick={chargerModele}
          onMouseOver={(e) => {
            e.target.style.transform = "translateY(-2px)";
            e.target.style.background = "#f3f4f6";
          }}
          onMouseOut={(e) => {
            e.target.style.transform = "translateY(0)";
            e.target.style.background = "#ffffff";
          }}
        >
          <span style={{ fontSize: "22px" }}>📋</span> Charger le modèle
        </button>
        <button
          style={S.btnAction}
          onClick={reinitialiserModele}
          onMouseOver={(e) => {
            e.target.style.transform = "translateY(-2px)";
            e.target.style.background = "#f3f4f6";
          }}
          onMouseOut={(e) => {
            e.target.style.transform = "translateY(0)";
            e.target.style.background = "#ffffff";
          }}
        >
          <span style={{ fontSize: "22px" }}>🔄</span> Réinitialiser
        </button>
      </div>

      {message && <p style={S.message}>{message}</p>}
    </div>
  );
}
